import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMonitors, getMonitorRuns, getMonitorAlertStatus } from '../services/api';
import StatusBadge from '../components/StatusBadge';

const Overview = () => {
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState({});
  const [alertStatuses, setAlertStatuses] = useState({});

  useEffect(() => {
    loadMonitors();
    const interval = setInterval(loadMonitors, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadMonitors = async () => {
    try {
      const response = await getMonitors();
      setMonitors(response.data);
      
      // Load latest status and alert status for each monitor
      const statusPromises = response.data.map(async (monitor) => {
        try {
          const [runsResponse, alertStatusResponse] = await Promise.all([
            getMonitorRuns(monitor._id, 1).catch(() => ({ data: [] })),
            getMonitorAlertStatus(monitor._id).catch(() => ({ data: null })),
          ]);
          const latestRun = runsResponse.data[0];
          return { 
            id: monitor._id, 
            status: latestRun?.ok || false,
            alertStatus: alertStatusResponse.data,
          };
        } catch (err) {
          return { id: monitor._id, status: null, alertStatus: null };
        }
      });
      
      const statusResults = await Promise.all(statusPromises);
      const statusMap = {};
      const alertStatusMap = {};
      statusResults.forEach(({ id, status, alertStatus }) => {
        statusMap[id] = status;
        alertStatusMap[id] = alertStatus;
      });
      setStatuses(statusMap);
      setAlertStatuses(alertStatusMap);
      setLoading(false);
    } catch (error) {
      console.error('Error loading monitors:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading monitors...</p>
      </div>
    );
  }

  const upCount = monitors.filter(m => statuses[m._id] === true).length;
  const downCount = monitors.filter(m => statuses[m._id] === false).length;
  const disabledCount = monitors.filter(m => !m.enabled).length;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Stats Header */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Monitors</p>
              <p className="text-3xl font-bold mt-1">{monitors.length}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Up</p>
              <p className="text-3xl font-bold mt-1">{upCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Down</p>
              <p className="text-3xl font-bold mt-1">{downCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-100 text-sm font-medium">Disabled</p>
              <p className="text-3xl font-bold mt-1">{disabledCount}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Monitors</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your uptime monitoring</p>
        </div>
        <Link
          to="/create"
          className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Monitor
        </Link>
      </div>

      {monitors.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No monitors yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">Get started by creating your first monitor</p>
            <Link
              to="/create"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-600 dark:hover:to-indigo-600 shadow-lg hover:shadow-xl transition-all"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Your First Monitor
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {monitors.map((monitor) => (
            <Link
              key={monitor._id}
              to={`/monitor/${monitor._id}`}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-600 transition-all transform hover:-translate-y-1 group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {monitor.name}
                  </h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono truncate">{monitor.url}</p>
                </div>
                <StatusBadge status={statuses[monitor._id]} enabled={monitor.enabled} />
              </div>
              <div className="space-y-3 mt-4">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <svg className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="capitalize">{monitor.type.replace('_', ' ')}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <svg className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Every {monitor.intervalMinutes} minutes
                </div>
                
                {/* Alert Status */}
                {alertStatuses[monitor._id] && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    {!alertStatuses[monitor._id].hasEmailConfig && (
                      <div className="flex items-center text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        No emails configured
                      </div>
                    )}
                    
                    {alertStatuses[monitor._id].consecutiveFailures > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">Failures:</span>
                        <span className={`font-semibold ${
                          alertStatuses[monitor._id].thresholdMet
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-orange-600 dark:text-orange-400'
                        }`}>
                          {alertStatuses[monitor._id].consecutiveFailures}/{alertStatuses[monitor._id].threshold}
                        </span>
                      </div>
                    )}
                    
                    {alertStatuses[monitor._id].hasOpenAlert && alertStatuses[monitor._id].nextEmailTime && (
                      <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                        <svg className="w-3 h-3 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Next email: {new Date(alertStatuses[monitor._id].nextEmailTime).toLocaleTimeString()}
                      </div>
                    )}
                    
                    {alertStatuses[monitor._id].thresholdMet && alertStatuses[monitor._id].hasEmailConfig && !alertStatuses[monitor._id].hasOpenAlert && (
                      <div className="flex items-center text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Alert should trigger
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    monitor.env === 'prod' 
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
                      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                  }`}>
                    {monitor.env.toUpperCase()}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Overview;
